import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { ControlledCheckbox } from '@/common/components/Fields/ControlledCheckbox/ControlledCheckbox';
import { ControlledDigitsField } from '@/common/components/Fields/ControlledDigitsField/ControlledDigitsField';
import { Form } from '@/common/components/Form/Form';
import { PageHeader } from '@/common/components/PageHeader/PageHeader';
import { Stack } from '@/common/components/Stack/Stack';
import { ROUTES } from '@/common/constants/routes';
import { useAuth } from '@/common/contexts/authentication/AuthProvider';
import { useAuthenticationContext } from '@/common/contexts/authentication/AuthenticationContext';
import { useBiometrics } from '@/common/hooks/useBiometrics';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { AuthService } from '@/common/integrations/menhir/employee/sessions/sessions.service';
import { AuthFormLayout } from '@/common/layouts/AuthLayout/AuthLayout';
import errorUtils from '@/common/utils/error';
import { notify } from '@/common/utils/toast/toast';
import { usePageViewTracking } from '@/web/hooks/usePageViewTracking';
import { zodResolver } from '@hookform/resolvers/zod';
import { Redirect, router } from 'expo-router';
import { TOTPFormData, otpFormSchema } from './OTPPage.schema';

export function OTPPage() {
  usePageViewTracking('login_page');
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const authenticationContext = useAuthenticationContext();
  const { biometricsAvailable } = useBiometrics();

  const methods = useForm({
    mode: 'onTouched',
    defaultValues: {
      otpCode: '',
      trustUserAgent: true,
    },
    resolver: zodResolver(otpFormSchema),
  });

  const onSubmit = async ({ trustUserAgent, otpCode }: TOTPFormData) => {
    try {
      const signInResponse = await AuthService.signIn({
        email: authenticationContext.email,
        password: authenticationContext.password,
        otp: otpCode,
        trustUserAgent,
      });

      authenticationContext.setContextCredentials({
        email: authenticationContext.email,
        password: authenticationContext.password,
        authState: signInResponse,
      });

      if ('user' in signInResponse) {
        const user = signInResponse;
        AnalyticsService.trackCustomEvent('login_otp_success', {
          trust_device: trustUserAgent,
        });

        if (biometricsAvailable) return router.navigate(ROUTES.BIOMETRICS);

        await signIn(user);
      }
    } catch (err: any) {
      const errorMessage = errorUtils.getErrorMessageKey(err?.errorCode);
      notify({
        type: 'error',
        message: t(errorMessage),
      });
      AnalyticsService.trackCustomEvent('login_otp_failed');
      methods.resetField('otpCode');
    }
  };

  if (
    !authenticationContext.authState ||
    !('reason' in authenticationContext.authState)
  ) {
    return <Redirect href={ROUTES.SIGNIN} />;
  }

  return (
    <AuthFormLayout
      footer={
        <>
          <Button
            type="submit"
            onPress={methods.handleSubmit(onSubmit)}
            loading={methods.formState.isSubmitting}
          >
            {t('authentication.mfa.send_code')}
          </Button>
        </>
      }
    >
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack gap="xl">
            <PageHeader
              title={
                authenticationContext.authState?.channel === 'sms' &&
                authenticationContext.authState?.reason === 'OTP_REQUIRED'
                  ? t('authentication.signin.form.mfa.otp.title', {
                      suffix: authenticationContext.authState.phoneLastDigits,
                    })
                  : t('authentication.signin.form.mfa.otp.email.title')
              }
            />
            <ControlledDigitsField name="otpCode" />
            <ControlledCheckbox
              name="trustUserAgent"
              label={t(
                'authentication.signin.form.mfa.otp.trust_user_agent_label'
              )}
            />
          </Stack>
        </Form>
      </FormProvider>
    </AuthFormLayout>
  );
}
